const GOOGLE_USERINFO_URL = 'https://www.googleapis.com/oauth2/v2/userinfo';

const SCOPES = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/spreadsheets',
].join(' ');

interface UserInfoResponse {
  id: string;
  email: string;
  name: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
}

type AuthCallback = (response: GISTokenResponse) => void;
type AuthErrorCallback = (error: { type: string; message: string }) => void;

class GoogleAuthService {
  private clientId: string;
  private tokenClient: GISTokenClient | null = null;
  private _onSuccess: AuthCallback | null = null;
  private _onError: AuthErrorCallback | null = null;

  constructor() {
    this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  }

  /**
   * Wait for GIS SDK to load
   */
  private waitForGIS(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.accounts) {
        resolve();
        return;
      }

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (typeof google !== 'undefined' && google.accounts) {
          clearInterval(interval);
          resolve();
        } else if (attempts > 50) {
          clearInterval(interval);
          reject(new Error('Google Identity Services SDK no se pudo cargar'));
        }
      }, 100);
    });
  }

  /**
   * Initialize the GIS token client
   */
  private async initTokenClient(): Promise<GISTokenClient> {
    if (this.tokenClient) return this.tokenClient;

    await this.waitForGIS();

    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: this.clientId,
      scope: SCOPES,
      callback: (response: GISTokenResponse) => {
        if (this._onSuccess) this._onSuccess(response);
      },
      error_callback: (error: { type: string; message: string }) => {
        if (this._onError) this._onError(error);
      },
    });

    return this.tokenClient;
  }

  /**
   * Request access token - opens Google popup
   * Returns a promise that resolves with the token response
   */
  async requestAccessToken(): Promise<GISTokenResponse> {
    const client = await this.initTokenClient();

    return new Promise((resolve, reject) => {
      this._onSuccess = (response: GISTokenResponse) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        resolve(response);
      };

      this._onError = (error: { type: string; message: string }) => {
        // 'popup_closed' means user closed the popup — not a real error
        if (error.type === 'popup_closed') {
          reject(new Error('popup_closed'));
        } else {
          reject(new Error(error.message || 'Error de autenticación'));
        }
      };

      client.requestAccessToken({ prompt: 'consent' });
    });
  }

  /**
   * Re-request token silently (no consent prompt)
   */
  async refreshToken(): Promise<GISTokenResponse> {
    const client = await this.initTokenClient();

    return new Promise((resolve, reject) => {
      this._onSuccess = (response: GISTokenResponse) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        resolve(response);
      };

      this._onError = (error: { type: string; message: string }) => {
        reject(new Error(error.message || 'Error al refrescar token'));
      };

      // prompt: '' means no consent screen, use existing grant
      client.requestAccessToken({ prompt: '' });
    });
  }

  /**
   * Get user info from Google
   */
  async getUserInfo(accessToken: string): Promise<UserInfoResponse> {
    const response = await fetch(GOOGLE_USERINFO_URL, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener información del usuario');
    }

    return response.json();
  }

  /**
   * Revoke the access token
   */
  async revokeToken(accessToken: string): Promise<void> {
    await this.waitForGIS();
    return new Promise((resolve) => {
      google.accounts.oauth2.revoke(accessToken, () => {
        resolve();
      });
    });
  }
}

export const googleAuthService = new GoogleAuthService();
